import axios from 'axios';
import Mutex from './mutex';

const libTag = '[LibLogger]';
const logBuffer = [];
const bufferMutex = new Mutex();
const userMutex = new Mutex();
const MAX_BUFFER_SIZE = 10;
const AUTO_FLUSH_INTERVAL = 10 * 1000; // 5sec

const platform = 'WEB';
const ua = navigator.userAgent;
const payloadBase = {
	platform,
	ua,
};
const globalExtraData = {};

export default class Logger {
	static get DEBUG() {
		return 'debug';
	}
	static get INFO() {
		return 'info';
	}
	static get WARN() {
		return 'warn';
	}
	static get ERROR() {
		return 'error';
	}
	static get CRITICAL() {
		return 'critical';
	}

	static async setUserData(displayName, role) {
		try {
			return await userMutex.dispatch(setUserData(displayName, role));
		} catch (e) {
			return false;
		}
	}
	static async clearUserData() {
		try {
			return await userMutex.dispatch(clearUserData());
		} catch (e) {
			return false;
		}
	}

	static setCallData(currentParticipant, participants) {
		try {
			setCallData(currentParticipant, participants);
		} catch (e) {}
	}

	static clearCallData() {
		try {
			clearCallData();
		} catch (e) {}
	}

	/**
	 *
	 * @param {string} severity Logger.DEBUG|Logger.INFO|Logger.WARN|Logger.ERROR|Logger.CRITICAL
	 * @param {string} msg Log message
	 * @param {string} className file/class name
	 * @param {string} functionName function that cought the error
	 * @param {string} topic global topic or flow (defaults to className)
	 * @param {object} extraData any other object you want to pass in.
	 * @param {Error} err The actual error object
	 * @param {string} category The category for the log entry
	 */
	static async log(
		severity,
		msg,
		className,
		functionName,
		topic = className,
		extraData = null,
		err = null,
		category = null
	) {
		const slug = window.location.pathname;
		const now = new Date();
		try {
			const log = {
				msg,
				className,
				functionName,
				topic,
				extraData: prepareExtraData(extraData),
				slug,
				time: now.toISOString(),
				severity,
				category,
			};
			const nullKeys = Object.keys(log).filter((key) => log[key] === null);
			nullKeys.forEach((key) => {
				delete log[key];
			});
			if (err) {
				// Sending stack + message in the log
				log.err = {
					message: err.message,
					stack: err.stack,
				};
			}
			if (window.DEBUG) {
				console.log(`${now.toLocaleString()} [${log.severity}]: ${log.msg}\n${JSON.stringify(log, null, 2)}`);
			}
			// Will not push **debug** messages in buffer.
			if (log.severity !== Logger.DEBUG) return await bufferMutex.dispatch(pushLog(log));
		} catch (e) {
			console.error(`${libTag}: Failed to push log ${e.message}`);
			return false;
		}
	}

	/**
	 * Will force sending all logs in logging buffer.
	 */
	static async flushAll() {
		return await bufferMutex.dispatch(postLogs(true));
	}
}

async function postLogs(flushAll = false) {
	const logs = logBuffer.splice(0, flushAll ? logBuffer.length : MAX_BUFFER_SIZE);
	if (!logs.length) return true;
	try {
		await axios.post(`/api/logs`, {
			...payloadBase,
			logs,
		});
		return true;
	} catch (e) {
		console.error(`${libTag}: Failed to post logs. Buffer length: ${logs.length}... will retry soon`);
		console.debug({ logBuffer });
		logBuffer.push(...logs); // Push logs into buffer - to retry later
		return false;
	}
}

async function pushLog(log) {
	logBuffer.push(log);
	if (logBuffer.length >= MAX_BUFFER_SIZE) {
		return await postLogs();
	}
	return true;
}

function prepareExtraData(extraData) {
	try {
		// merge with global extra data
		const mergedExtraData = { ...extraData, ...globalExtraData };
		// add memory
		const { jsHeapSizeLimit, totalJSHeapSize, usedJSHeapSize } = window.performance.memory;
		mergedExtraData._memory = { jsHeapSizeLimit, totalJSHeapSize, usedJSHeapSize };
		// add window state
		mergedExtraData._window = {
			hasFocus: document.hasFocus(),
			visibilityState: document.visibilityState,
		};
		// return
		return mergedExtraData;
	} catch (e) {
		console.warn('logger', 'prepareExtraData failed');
		return extraData;
	}
}

function setUserData(name, role) {
	payloadBase.user = {
		name,
		role,
	};
	return true;
}

function clearUserData() {
	delete payloadBase.user;
}

function setCallData(currentParticipant, participants) {
	globalExtraData._call = {
		currentParticipant: { role: currentParticipant.role },
		numberOfParticipants: participants.length + 1, // +1 for the current participant
	};
	return true;
}

function clearCallData() {
	delete globalExtraData.call;
}

function getNameFromStorage() {
	let name = window.localStorage.getItem('name');
	if (name) {
		name = name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
	} else {
		name = 'guest';
	}
	return name;
}

setInterval(async () => {
	if (logBuffer.length < MAX_BUFFER_SIZE) {
		return Logger.flushAll();
	}
}, AUTO_FLUSH_INTERVAL);
